/**
 * My schedule setup checklist (DNKADM-0492 / DNKAPI-0714): what a new coach does so
 * members can book them. Three items:
 *
 *  1. **Set your weekly hours** — the only required item. It ticks from the server's
 *     `setupHealth` (no `NO_HOURS` and no `NO_LOCATION`). When the tenant lets coaches set
 *     their own hours it links to the editor on this page; otherwise it says an owner or
 *     admin sets them.
 *  2. **Connect Google Calendar** — plain steps. Dink can't connect it for the coach.
 *  3. **Check your profile** — the name, email and phone on their coach record.
 *
 * The card can be dismissed. That is remembered per login in this browser's
 * localStorage (try/catch guarded: unavailable storage means it shows again next time).
 * It renders nothing when the server sent no `setupHealth`.
 */
import { useState, type ReactNode } from 'react';
import { Box, Button, Link as MuiLink, Paper, Stack, Typography } from '@mui/material';
import { Icon } from '@/theme';
import { staffDisplayName, type StaffSetupHealth, type V2Staff } from '@/api/staff';
import { hoursAreSet } from '@/features/staff/setupHealth';

/** The weekly-hours section id on My schedule, which the hours item scrolls to. */
export const MY_HOURS_SECTION_ID = 'my-weekly-hours';

const STORAGE_PREFIX = 'dink_admin_coach_setup_dismissed:';

/**
 * The storage key for one login's dismissal: the coach record's linked login, plus the
 * record id so the same login on another tenant's coach record is asked separately.
 */
function coachSetupDismissKey(staff: Pick<V2Staff, 'id' | 'authUserId'>): string {
  return `${STORAGE_PREFIX}${staff.authUserId ?? 'none'}:${staff.id}`;
}

function readDismissed(key: string): boolean {
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}

function writeDismissed(key: string): void {
  try {
    localStorage.setItem(key, 'true');
  } catch {
    /* localStorage unavailable — the dismissal lasts until the page reloads */
  }
}

function scrollToHours() {
  document.getElementById(MY_HOURS_SECTION_ID)?.scrollIntoView?.({
    behavior: 'smooth',
    block: 'start',
  });
}

export function CoachSetupChecklist({
  staff,
  health,
  canEditHours,
}: {
  staff: V2Staff;
  /** The latest setup health (the screen re-reads it after an hours save). */
  health: StaffSetupHealth | undefined;
  /** The tenant's "Coaches can set their own hours" switch, as My schedule read it. */
  canEditHours: boolean;
}) {
  const key = coachSetupDismissKey(staff);
  const [dismissed, setDismissed] = useState(() => readDismissed(key));

  if (health === undefined || dismissed) return null;
  const hoursDone = hoursAreSet(health) === true;
  const noLocation = health.missing.includes('NO_LOCATION');

  let hoursDetail: ReactNode;
  if (hoursDone) {
    hoursDetail = 'Done. Members can see when you work.';
  } else if (noLocation) {
    hoursDetail =
      'You aren’t assigned to a location yet. Ask an owner or admin to assign you, then set your hours.';
  } else if (canEditHours) {
    hoursDetail = (
      <>
        Add your working hours for each location, then save.{' '}
        <MuiLink
          component="button"
          type="button"
          onClick={scrollToHours}
          sx={{ fontSize: 'inherit' }}
        >
          Go to weekly hours
        </MuiLink>
      </>
    );
  } else {
    hoursDetail =
      'An owner or admin sets your weekly hours. Ask them to add your hours so members can book you.';
  }

  const profileParts = [staffDisplayName(staff), staff.email, staff.phone].filter(
    (part): part is string => typeof part === 'string' && part.length > 0,
  );

  return (
    <Paper
      elevation={1}
      sx={{ px: 2.75, py: 2.25, maxWidth: 640, mt: 3 }}
      data-testid="coach-setup-checklist"
      component="section"
      aria-label="Get ready to take bookings"
    >
      <Stack
        direction="row"
        sx={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}
      >
        <Box>
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
            Get ready to take bookings
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Only your weekly hours are required. The rest helps your bookings go smoothly.
          </Typography>
        </Box>
        <Button
          size="small"
          onClick={() => {
            writeDismissed(key);
            setDismissed(true);
          }}
          aria-label="Dismiss setup checklist"
          sx={{ flexShrink: 0 }}
        >
          Dismiss
        </Button>
      </Stack>

      <Stack component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 2, gap: 1.75 }}>
        <ChecklistItem
          testId="checklist-hours"
          title="Set your weekly hours"
          required
          done={hoursDone}
        >
          {hoursDetail}
        </ChecklistItem>
        <ChecklistItem testId="checklist-calendar" title="Connect Google Calendar" icon="event">
          Dink can’t connect your calendar for you. Sign in to Journix, open Settings › Calendars ›
          Connections, and connect your Google account. Then ask an owner or admin to pick your
          calendar on the Staff screen.
        </ChecklistItem>
        <ChecklistItem testId="checklist-profile" title="Check your profile" icon="person">
          {`Make sure this is right: ${profileParts.join(' · ')}. An owner or admin can change it on the Staff screen.`}
        </ChecklistItem>
      </Stack>
    </Paper>
  );
}

/**
 * One checklist row. A `required` row shows a done / not-done mark from `done`; the
 * other rows are guidance the server does not track, so they show a plain `icon`.
 */
function ChecklistItem({
  testId,
  title,
  required = false,
  done = false,
  icon,
  children,
}: {
  testId: string;
  title: string;
  required?: boolean;
  done?: boolean;
  icon?: string;
  children: ReactNode;
}) {
  return (
    <Box
      component="li"
      data-testid={testId}
      data-done={done ? 'true' : 'false'}
      sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}
    >
      {required ? (
        <Icon
          name={done ? 'check_circle' : 'radio_button_unchecked'}
          title={done ? 'Done' : 'Not done'}
          sx={{ fontSize: 20, mt: '1px', color: done ? 'success.main' : 'text.secondary' }}
        />
      ) : (
        <Icon name={icon ?? 'info'} sx={{ fontSize: 20, mt: '1px', color: 'text.secondary' }} />
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
          {title}
          {required && (
            <Typography
              component="span"
              sx={{ fontSize: 12, fontWeight: 500, color: 'text.secondary', ml: 1 }}
            >
              Required
            </Typography>
          )}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {children}
        </Typography>
      </Box>
    </Box>
  );
}
